// frontend/src/components/EarningsAnalytics.jsx
// ✅ Uses backend data for accurate earnings (includes all usage, not just own)
// ✅ Stacked bar: image vs video royalties in distinct colors
// ✅ Emojis removed for a cleaner design

import React from "react";
import { getIdentityHeaders } from "../lib/identity";
import { API_BASE, fmtPi } from "../lib/api";

const api = (path) => (API_BASE ? `${API_BASE}${path}` : path);

const brand = {
  purple: "#6D5EF9",
  purpleDark: "#5B4EE8",
  purpleTint: "#F3F0FF",
  purpleBorder: "#E7E2FF",
  neutralText: "#111827",
  neutralBorder: "#E5E7EB",
};

// Image royalties = purple, Video royalties = teal. Distinct, on-brand.
const COLOR_IMAGE = "#6D5EF9";
const COLOR_VIDEO = "#14B8A6";

export default function EarningsAnalytics() {
  const [loading, setLoading] = React.useState(true);
  const [period, setPeriod] = React.useState("30d");
  const [rows, setRows] = React.useState([]);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(api(`/api/creator/royalties/by-style?period=${period}`), {
          headers: getIdentityHeaders(),
        });

        if (!res.ok) {
          throw new Error(`Failed to load: ${res.status}`);
        }

        const data = await res.json();

        if (alive) {
          const items = Array.isArray(data.items) ? data.items : [];
          const formatted = items.map((item) => ({
            ...item,
            name: formatStyleName(item.name || item.id),
            // Defensive defaults so older backend responses don't break the UI
            imageEarningsPi: Number(item.imageEarningsPi ?? item.earningsPi ?? 0),
            videoEarningsPi: Number(item.videoEarningsPi ?? 0),
            imageUses: Number(item.imageUses ?? item.uses ?? 0),
            videoUses: Number(item.videoUses ?? 0),
          }));
          setRows(formatted);
        }
      } catch (e) {
        console.error("Failed to load earnings analytics:", e);
        if (alive) setError(e.message || "Failed to load");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [period]);

  const totalEarnings = rows.reduce((s, r) => s + r.earningsPi, 0);
  const totalImage = rows.reduce((s, r) => s + r.imageEarningsPi, 0);
  const totalVideo = rows.reduce((s, r) => s + r.videoEarningsPi, 0);
  const maxEarnings = Math.max(...rows.map((r) => r.earningsPi), 0.01);

  return (
    <section className="cd-analytics-section">
      <div className="cd-card">
        <StyleTag />

        <div className="analytics-header">
          <h2 className="analytics-title">Earnings by Style</h2>
          <select
            className="period-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="all">All time</option>
          </select>
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : error ? (
          <div className="error-message">
            {error}
            <div style={{ fontSize: "12px", marginTop: "8px", opacity: 0.8 }}>
              Make sure the /api/creator/royalties/by-style endpoint is reachable.
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="empty">No earnings data yet</div>
        ) : (
          <>
            <div className="summary-row">
              <div className="summary-item">
                <div className="summary-label">Period earnings</div>
                <div className="summary-value">{fmtPi(totalEarnings)}</div>
              </div>
              <div className="summary-item">
                <div className="summary-label">Active styles</div>
                <div className="summary-value">{rows.length}</div>
              </div>
            </div>

            {/* Legend */}
            <div className="legend">
              <div className="legend-item">
                <span className="legend-swatch" style={{ background: COLOR_IMAGE }} />
                <span className="legend-label">Images ({fmtPi(totalImage)})</span>
              </div>
              <div className="legend-item">
                <span className="legend-swatch" style={{ background: COLOR_VIDEO }} />
                <span className="legend-label">Videos ({fmtPi(totalVideo)})</span>
              </div>
            </div>

            <div className="styles-list">
              {rows.map((row, idx) => {
                const totalPct = (row.earningsPi / maxEarnings) * 100;
                const imageShare =
                  row.earningsPi > 0 ? row.imageEarningsPi / row.earningsPi : 1;
                const videoShare =
                  row.earningsPi > 0 ? row.videoEarningsPi / row.earningsPi : 0;

                return (
                  <div key={row.id} className="style-row">
                    <div className="style-header">
                      <div className="style-info">
                        <span className="style-rank">{idx + 1}</span>
                        <span className="style-name">{row.name}</span>
                      </div>
                      <span className="style-total">{fmtPi(row.earningsPi)}</span>
                    </div>

                    {/* Stacked bar: image segment + video segment */}
                    <div
                      className="earnings-track"
                      style={{ width: `${Math.max(totalPct, 5)}%` }}
                    >
                      {imageShare > 0 && (
                        <div
                          className="earnings-seg"
                          style={{
                            background: COLOR_IMAGE,
                            width: `${imageShare * 100}%`,
                          }}
                          title={`Images: ${fmtPi(row.imageEarningsPi)}`}
                        />
                      )}
                      {videoShare > 0 && (
                        <div
                          className="earnings-seg"
                          style={{
                            background: COLOR_VIDEO,
                            width: `${videoShare * 100}%`,
                          }}
                          title={`Videos: ${fmtPi(row.videoEarningsPi)}`}
                        />
                      )}
                    </div>

                    <div className="style-meta">
                      <span>
                        {row.imageUses} image{row.imageUses !== 1 ? "s" : ""}
                        {row.videoUses > 0 && (
                          <>
                            {" · "}
                            {row.videoUses} video{row.videoUses !== 1 ? "s" : ""}
                          </>
                        )}
                      </span>
                      <span className="royalty-rate">{row.ratePct}% royalty rate</span>
                    </div>

                    {row.updatedAt && (
                      <div className="last-earned">Last earned: {timeAgo(row.updatedAt)}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function formatStyleName(style) {
  const normalized = String(style || "").toLowerCase().trim();
  if (!normalized || normalized === "default") return "Standard";
  return normalized
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function timeAgo(dateStr) {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function StyleTag() {
  return (
    <style>{`
.analytics-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  flex-wrap: wrap;
  gap: 10px;
}

.analytics-title {
  font-size: 20px;
  font-weight: 800;
  margin: 0;
  color: ${brand.neutralText};
}

.period-select {
  height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid ${brand.neutralBorder};
  background: white;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
  padding: 16px;
  background: ${brand.purpleTint};
  border: 1px solid ${brand.purpleBorder};
  border-radius: 12px;
}

@media (max-width: 600px) {
  .summary-row { grid-template-columns: 1fr; }
}

.summary-item { text-align: center; }

.summary-label {
  font-size: 13px;
  color: #6B7280;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.summary-value {
  font-size: 24px;
  font-weight: 900;
  color: ${brand.neutralText};
}

.legend {
  display: flex;
  gap: 18px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 7px;
}

.legend-swatch {
  width: 14px;
  height: 14px;
  border-radius: 4px;
  display: inline-block;
}

.legend-label {
  font-size: 13px;
  font-weight: 700;
  color: #6B7280;
}

.styles-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.style-row {
  background: #FAFAFA;
  border: 1px solid ${brand.neutralBorder};
  border-radius: 12px;
  padding: 14px;
}

.style-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.style-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.style-rank {
  font-size: 13px;
  font-weight: 800;
  color: #9CA3AF;
  min-width: 18px;
}

.style-name {
  font-size: 16px;
  font-weight: 800;
  color: ${brand.neutralText};
}

.style-total {
  font-size: 15px;
  font-weight: 900;
  color: ${brand.neutralText};
}

.earnings-track {
  height: 28px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  margin-bottom: 10px;
  min-width: 40px;
  box-shadow: 0 1px 4px rgba(17, 24, 39, 0.08);
  transition: width 0.3s ease;
}

.earnings-seg {
  height: 100%;
  transition: width 0.3s ease;
}

.style-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #6B7280;
  font-weight: 600;
  flex-wrap: wrap;
  gap: 8px;
}

.royalty-rate {
  background: ${brand.purpleTint};
  color: ${brand.purple};
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 800;
  font-size: 12px;
}

.last-earned {
  margin-top: 8px;
  font-size: 12px;
  color: #9CA3AF;
  font-weight: 600;
}

.loading,
.empty {
  text-align: center;
  padding: 40px;
  color: #6B7280;
  font-weight: 600;
}

.error-message {
  text-align: center;
  padding: 24px;
  background: #FEF2F2;
  border: 1px solid #FCA5A5;
  border-radius: 12px;
  color: #991B1B;
  font-weight: 600;
}
    `}</style>
  );
}